import type {
    Membership,
    MembershipChoice,
    MembershipPermissionsType,
    MembershipResourceCrudPermission,
    OrgEntityType,
    AccountCreditBalances,
    SiteMembershipContext
} from '~/types';
import { useCustomAuth } from '~/composables';
import { castArray as lo_castArray } from 'es-toolkit/compat';

export function useUserLite() {
    const {
        isLoggedIn,
        getSession,
        sessionObj: userObj
    } = useCustomAuth();

    const defaultBillingAddressId = computed<Nullable<string>>(() => userObj.value?.defaultBillingAddressUid ?? null);
    const defaultShippingAddressId = computed<Nullable<string>>(() => userObj.value?.defaultShippingAddressUid ?? null);

    const currentMembershipChoice = computed<Nullable<MembershipChoice>>(() => {
        const choices = userObj.value?.membershipChoices;
        if (!choices) return null;
        return choices.find((o) => !!o.isSelected) ?? null;
    });

    const currentMembership = computed<Nullable<Membership>>(() => currentMembershipChoice.value?.membership ?? null);
    const currentMembershipCtx = computed<Nullable<SiteMembershipContext>>(() => currentMembershipChoice.value?.context ?? null);
    const currentMembershipUid = computed<Nullable<string>>(() => currentMembershipChoice.value?.membershipUid ?? null);

    const accountCreditBalances = computed<AccountCreditBalances>(() => ({
        personal: userObj.value?.accountCreditBalance ?? 0,
        entity: currentMembership.value?.entity?.accountCreditBalance ?? 0
    }));
    const entityUid = computed<Nullable<string>>(() => currentMembership.value?.entity?.uid ?? null);
    const entityType = computed<Nullable<OrgEntityType>>(() => currentMembership.value?.entity?.entityType ?? null);

    const isRetailer = computed<boolean>(() => currentMembership.value?.entity?.isRetail ?? false);

    function getCurrentMembership(): Nullable<Membership> {
        return currentMembership?.value ?? null;
    }

    function isOrgMemberAllowed(
        type: MembershipPermissionsType,
        permission?: Nilish<string>,
        cruds?: MembershipResourceCrudPermission | MembershipResourceCrudPermission[]
    ): boolean {
        const permissions = currentMembership.value?.permissions;

        if (!permission || !permissions) {
            return false;
        }

        switch (type) {
            case 'resource':
                const perm = permissions.resources?.[permission]?.permissions;

                if (!cruds || !perm) return false;

                cruds = lo_castArray(cruds);
                return cruds.every((crud) => perm.includes(crud));
            case 'page':
                return !!permissions?.pages?.includes(permission);
            case 'flag':
                return !!permissions?.flags?.includes(permission);
        }

        return false;
    }

    function canOrgMemberPerform(
        resource: string,
        action: string | string[]
    ): boolean {
        const permissions = currentMembership.value?.permissions;

        if (!permissions) return false;

        const perm = permissions.resources?.[resource]?.actions;

        if (!perm) return false;

        action = lo_castArray(action);
        return action.every((act) => perm.includes(act));
    }

    function fetchUser(force = false) {
        return getSession(force);
    }

    function getIsRetailer(): boolean {
        const currentMembershipObj = getCurrentMembership();

        if (currentMembershipObj) {
            return currentMembershipObj?.entity?.isRetail ?? false;
        }

        return false;
    }

    return {
        userObj,
        fetchUser,
        isLoggedIn,

        /**
        *  @deprecated use getIsRetailer instead
        */
        getIsReseller: getIsRetailer,
        /**
        *  @deprecated use isRetailer instead
        */
        isReseller: isRetailer,

        isRetailer,
        getIsRetailer,
        defaultBillingAddressId,
        defaultShippingAddressId,
        accountCreditBalances,
        entityUid,
        entityType,
        currentMembership,
        currentMembershipCtx,
        currentMembershipUid,
        currentMembershipChoice,
        getCurrentMembership,
        isOrgMemberAllowed,
        canOrgMemberPerform
    };
}
